import { z } from 'zod'
import { apiTokenScopeSchema } from '../enums/api-token-scope.enum.js'

/** How long a new token lives. `never` is available but has to be chosen. */
export const API_TOKEN_LIFETIMES = ['30d', '90d', '365d', 'never'] as const
export const apiTokenLifetimeSchema = z.enum(API_TOKEN_LIFETIMES)
export type ApiTokenLifetime = z.infer<typeof apiTokenLifetimeSchema>

/** One scope granted to a token, and whether it may write within it. */
export const apiTokenGrantInputSchema = z
  .object({
    scope: apiTokenScopeSchema,
    /** Write covers every method but GET. `readOnly` on the token overrides this. */
    write: z.boolean().default(false),
  })
  .strict()
export type ApiTokenGrantInput = z.infer<typeof apiTokenGrantInputSchema>

/**
 * Create a personal API token.
 *
 * A token carries the access its owner's role carries, resolved live on every
 * request — scopes narrow that, they never widen it. At least one scope must be
 * named: there is no wildcard, and a token with no grants can call nothing, so
 * defaulting to "everything" would quietly undo the point of asking.
 *
 * See `docs/api-token-scopes.md` for what each scope covers.
 */
export const createApiTokenInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Give the token a name.').max(80, 'That name is too long.'),
    /** GET only, whatever the grants say. Defaults to the safer of the two. */
    readOnly: z.boolean().default(true),
    /** An expiry has to be chosen rather than defaulted into never. */
    lifetime: apiTokenLifetimeSchema,
    /** What the token may touch. One scope minimum; duplicates are refused. */
    scopes: z
      .array(apiTokenGrantInputSchema)
      .min(1, 'Choose at least one thing this token may access.')
      .refine(
        (list) => new Set(list.map((g) => g.scope)).size === list.length,
        'Each scope can only be listed once.',
      ),
    /**
     * Permits clearing a venue's events, wiping imported data and deleting a
     * venue — on top of write on their scope. Off unless asked for, because none
     * of the three can be undone.
     */
    allowDestructive: z.boolean().default(false),
  })
  .strict()
export type CreateApiTokenInput = z.infer<typeof createApiTokenInputSchema>

/** How long each lifetime lasts, in days. `never` has no expiry at all. */
export function apiTokenLifetimeDays(lifetime: ApiTokenLifetime): number | null {
  switch (lifetime) {
    case '30d':
      return 30
    case '90d':
      return 90
    case '365d':
      return 365
    case 'never':
      return null
  }
}
